import React, {
    useState,
    useEffect,
    useReducer,
    useContext,
    useCallback,
    useMemo,
} from "react";
import mapFormData from "../utils/map-form-data";
import assembleDto from "../utils/assemble-dto";
import getStep from "../utils/get-current-step";
import { FORM_ERROR } from "final-form";
import scrollToTop from "../utils/scroll-to-top";
import { initialState, reducer } from "../flow-reducer";
import { endpoints } from "../config";
import {
    findGetParameter,
    checkIfDebug,
    initialValuesFromInputs,
} from "../utils/form-utils";
import {
    trackSubmitStep,
    trackedSteps,
    getNextStepNameAndNumber,
    trackBackButtonClicked,
    setInitialTrackingValues,
    setFlagsForTracking,
} from "../utils/tracking/tracking";
import {
    callServer,
    redirectGet,
    handleSubmitAndPayment,
} from "../flow-api-methods";
import useModal from "./useModal";

let flowSteps = [];

function useFlow(
    savedSteps = [],
    savedInitialValues = {},
    savedState = initialState
) {
    const isMobileView = useMemo(() => window.innerWidth < 769, []); // Declare early, memoized once on mount
    const { showModal } = useModal();
    const [steps, setSteps] = useState(savedSteps);
    const [state, dispatch] = useReducer(reducer, savedState);
    const [initialValues, setInitialValues] = useState(savedInitialValues);
    const [form, setForm] = useState();
    const [error, setError] = useState();
    const [allSteps, setAllSteps] = useState([]);
    const [savedStepData, setSavedStepData] = useState([]);

    const { currentIndex } = state;
    const currentStep = steps[currentIndex];

    const getFormValues = useCallback((flowSteps) => {
        let allFormValues = {};
        flowSteps.forEach((step) => {
            if (step?.formValues) {
                allFormValues = { ...allFormValues, ...step.formValues };
            }
        });
        return allFormValues;
    }, []);

    // --- Unified goTo ---
    const goTo = useCallback(
        async (index, valuesOrSkipTracking) => {
            if (currentIndex === index) return;

            const skipTracking =
                typeof valuesOrSkipTracking === "boolean"
                    ? valuesOrSkipTracking
                    : false;
            const values =
                typeof valuesOrSkipTracking === "object"
                    ? valuesOrSkipTracking
                    : undefined;

            // Save current step data if values provided
            if (values) {
                const existingStepIndex = savedStepData.findIndex(
                    (step) =>
                        step.step === currentIndex &&
                        step.currentStepComponent === currentStep.Component &&
                        step.variant === currentStep.Variant
                );

                if (existingStepIndex !== -1) {
                    const updatedSavedStepData = [...savedStepData];
                    updatedSavedStepData[existingStepIndex] = {
                        ...updatedSavedStepData[existingStepIndex],
                        formValues: values,
                    };
                    setSavedStepData(updatedSavedStepData);
                } else {
                    setSavedStepData([
                        ...savedStepData,
                        {
                            step: currentIndex,
                            currentStepComponent: currentStep.Component,
                            variant: currentStep.Variant,
                            formValues: values,
                        },
                    ]);
                }

                // Map and store form data in flowSteps
                const formData = mapFormData(
                    values,
                    currentStep,
                    getFormValues(flowSteps)
                );
                if (
                    flowSteps.length > currentIndex &&
                    flowSteps[currentIndex]?.Component ===
                        currentStep.Component &&
                    flowSteps[currentIndex]?.Variant === currentStep.Variant
                ) {
                    flowSteps[currentIndex].formValues = values;
                } else {
                    while (flowSteps.length < currentIndex) {
                        flowSteps.push(null);
                    }
                    flowSteps[currentIndex] = { ...formData };
                }
            }

            // Handle navigation
            if (steps[index]) {
                await scrollToTop();

                const clickedStep = steps[index];
                if (!skipTracking && trackedSteps[clickedStep.Component]) {
                    // Example: trackProgressBar(trackedSteps[clickedStep.Component]);
                }

                const savedData = savedStepData.find(
                    (step) =>
                        step.step === index &&
                        step.currentStepComponent === clickedStep.Component &&
                        step.variant === clickedStep.Variant
                );

                if (flowSteps[index]) {
                    form.initialize(flowSteps[index].formValues);
                } else if (savedData) {
                    form.initialize(savedData.formValues);
                } else {
                    form.initialize(clickedStep.formValues || {});
                }

                dispatch({ type: "GO_TO_STEP", value: index });
            }
        },
        [currentIndex, currentStep, form, savedStepData, steps, getFormValues]
    );

    // --- Unified goBack ---
    const goBack = useCallback(
        (values) => {
            if (currentIndex === 0) return;
            goTo(currentIndex - 1, values);
            trackBackButtonClicked(steps[currentIndex - 1]);
        },
        [currentIndex, goTo, steps]
    );

    // --- submitStep ---
    async function submitStep(formValues, formInstance) {
        dispatch({ type: "START_LOADING" });
        if (state.submitPending) return;

        await scrollToTop();
        const nextIndex = currentIndex + 1;

        if (!form) setForm(formInstance);
        const formIsPristine = formInstance.getState().pristine;
        const formDataIsReusable =
            formIsPristine ||
            (!formIsPristine && currentIndex === 0 && formValues.CPRPristine);

        const formData = mapFormData(formValues, currentStep);

        setAllSteps([
            ...allSteps,
            {
                step: currentStep.Component,
                formValues: getFormValues([
                    { Variant: currentStep.Variant, formValues: formData },
                ]),
            },
        ]);

        try {
            let nextStep;
            if (
                flowSteps.length &&
                flowSteps[currentIndex] &&
                formData.Component === flowSteps[currentIndex].Component &&
                formData.Variant === flowSteps[currentIndex].Variant &&
                !formDataIsReusable
            ) {
                flowSteps = flowSteps.slice(0, currentIndex);
            } else if (
                formDataIsReusable &&
                !formIsPristine &&
                currentIndex === 0 &&
                formValues.CPRPristine &&
                flowSteps[0]
            ) {
                flowSteps[0].formValues = formValues;
            }

            const response = await callServer({
                body: {
                    FrontendStateSnapShotJson: JSON.stringify({}),
                    FlowSteps: [...flowSteps, formData],
                },
            });

            if (!response?.Resources) {
                console.log("No response from server about next step.");
                return setError(nextStep.error);
            }
            nextStep = JSON.parse(response.Resources);
            if (nextStep.error) return setError(nextStep.error);

            let newInitialValues;
            if (
                steps[nextIndex] &&
                flowSteps[nextIndex] &&
                steps[nextIndex].Component === flowSteps[nextIndex].Component &&
                steps[nextIndex].Variant === flowSteps[nextIndex].Variant
            ) {
                if (formDataIsReusable) {
                    const mergedFormValues = flowSteps.reduce(
                        (sum, curr) => ({
                            ...sum,
                            ...curr.formValues,
                        }),
                        {}
                    );
                    newInitialValues = { ...formValues, ...mergedFormValues };
                } else {
                    newInitialValues = {
                        ...formValues,
                        ...flowSteps[nextIndex].formValues,
                    };
                }
            } else {
                flowSteps = flowSteps.slice(0, currentIndex);
                flowSteps.push({ ...formData });
                newInitialValues = formValues;
            }

            const SkipAkasseFlag =
                findGetParameter("SkipAkasseFlag") ||
                findGetParameter("SkipAkasse") ||
                undefined;

            const hasUnsubmittedData = savedStepData.find(
                (co) =>
                    co.step === nextIndex &&
                    co.variant === nextStep.Variant &&
                    co.currentStepComponent === nextStep.Component
            );
            if (
                steps[nextIndex] &&
                steps[nextIndex].Component === nextStep.Component &&
                steps[nextIndex].Variant === nextStep.Variant &&
                hasUnsubmittedData
            ) {
                newInitialValues = {
                    ...hasUnsubmittedData.formValues,
                    ...newInitialValues,
                };
            }

            getNextStepNameAndNumber(nextStep);
            setSteps([...steps.slice(0, nextIndex), nextStep]);
            setInitialValues({
                ...initialValuesFromInputs(
                    nextStep.Inputs,
                    SkipAkasseFlag,
                    isMobileView
                ),
                ...newInitialValues,
            });
            formInstance.initialize({
                ...initialValuesFromInputs(nextStep.Inputs),
                ...newInitialValues,
            });

            dispatch({ type: "NEXT_STEP" });
            history.pushState({}, nextStep.Title);

            if (trackedSteps[currentStep.Component]) {
                trackSubmitStep({ step: currentStep, data: formValues });
            }
        } catch (err) {
            console.log(err);
            showServerErrorModal();
            dispatch({ type: "STEP_CHANGE_ENDED" });
            return { [FORM_ERROR]: window.Config.Labels.ErrorSubmit };
        }
    }

    async function submitForm(formValues) {
        const allFormData = flowSteps.reduce(
            (allData, flowStep) => ({
                ...allData,
                ...flowStep.formValues,
            }),
            {}
        );

        if (trackedSteps[currentStep.Component]) {
            trackSubmitStep({ step: currentStep, data: formValues });
        }

        try {
            const dto = assembleDto(
                { ...allFormData, ...formValues },
                flowSteps,
                steps
            );
            const res = await handleSubmitAndPayment(
                dto,
                formValues,
                allSteps,
                flowSteps
            );

            if (!res?.SubmitResponse || !res.Success) {
                dispatch({ type: "STEP_CHANGE_ENDED" });
                return { [FORM_ERROR]: window.Config.Labels.ErrorSubmit };
            }
        } catch (err) {
            console.log(err);
            redirectGet(true);
            dispatch({ type: "STEP_CHANGE_ENDED" });
            return { [FORM_ERROR]: window.Config.Labels.ErrorSubmit };
        }
    }

    async function getInitialStep() {
        const SkipAkasseFlag =
            findGetParameter("SkipAkasseFlag") ||
            findGetParameter("SkipAkasse") ||
            undefined;
        const SkipUnionFlag = findGetParameter("SkipUnionFlag") || undefined;
        const UnderEducationFlag =
            findGetParameter("UnderEducation") || undefined;

        let Component = findGetParameter("Component") || "DefaultComponent";
        let Variant = findGetParameter("Variant") || "0";
        let endPoint =
            Component === "DefaultComponent"
                ? endpoints.nextStep
                : endpoints.specificComponent;

        const data = {
            Component,
            Variant,
            SkipAkasseFlag,
            SkipUnionFlag,
            UnderEducationFlag,
            formValues: { SkipUnionFromURL: SkipUnionFlag },
        };

        const response = await callServer({
            body: {
                FrontendStateSnapShotJson: JSON.stringify({}),
                FlowSteps: [...flowSteps, data],
            },
            endpoint: endPoint,
        });

        const nextStep = JSON.parse(response.Resources);
        setFlagsForTracking(SkipUnionFlag, UnderEducationFlag);
        setInitialTrackingValues();
        setSteps([...steps, nextStep]);

        setInitialValues(initialValuesFromInputs(nextStep.Inputs));

        dispatch({ type: "NEXT_STEP" });
        dispatch({ type: "STEP_CHANGE_ENDED" });

        if (window.Maintenance) {
            showMaintenanceModal();
        }
    }

    function showServerErrorModal() {
        const msg =
            window.Config.Labels.NoMkatExitContent ||
            "Ups! Der skete en fejl. Prøv venligst igen senere.";
        const title = window.Config.Labels.NoMkatExitTitle || "Fejl";
        showModal(msg, title, true);
    }

    function showMaintenanceModal() {
        showModal(
            window.Config.Labels.MaintenanceContent,
            window.Config.Labels.MaintenanceTitle,
            true
        );
    }

    useEffect(() => {
        getInitialStep();
    }, []);
    useEffect(() => {
        document.activeElement.blur();
    }, [currentIndex]);

    // --- Render currentStepComponent ---
    let currentStepComponent = null;
    if (state.showStep && currentStep) {
        currentStepComponent = getStep({
            ...currentStep,
            goBack,
            goTo,
            submitForm,
            steps,
            flowSteps,
            submitPending: state.submitPending,
        });
    }

    return {
        goBack,
        goTo,
        currentStep,
        steps,
        currentStepComponent,
        initialValues,
        dispatch,
        state,
        submitStep,
        submitForm,
        error,
        allSteps,
    };
}

export default useFlow;
